// Which supplementary sides Learn offers alongside the primary prompt — the
// tabs below the card and the (currently unreachable, kept for parity) "Show
// All" overlay. Extracted from LearnPage so the exclusion rules are
// unit-testable without rendering the page.

/** Minimal shape any side needs for these filters. */
export interface LearnSide {
  label: string
  content: string
  kind?: string | null
}

const IMAGE_LABELS_LEARN = new Set(['Image', 'Image URL'])

/** Image sides are always hidden in Learn — they reveal the answer visually. */
export function isImageSide(s: Pick<LearnSide, 'label' | 'content'>): boolean {
  return IMAGE_LABELS_LEARN.has(s.label) || s.content.startsWith('data:image/')
}

/** Labels allowed to appear as supplementary content during a Learn session.
 * Keeps CSV metadata pollution (Entry_Id, Lemma, ...) out of the study UI. */
export const LEARN_SIDE_ALLOWLIST = new Set([
  'Term', 'Definition', 'Example', 'Translation',
  'Image', 'Image URL', 'Audio', 'Link', 'Dialog', 'Notes',
])

export function isAllowedSide(s: Pick<LearnSide, 'label'>): boolean {
  return LEARN_SIDE_ALLOWLIST.has(s.label)
}

/**
 * Supplementary sides for **definition mode** — used for both the tab list
 * and the "Show All" overlay (ADR-068 Prompt C).
 *
 * A card studies in definition mode either because it has no Example at all,
 * or because `buildFillInBlank` could not locate a blank in one it has: an
 * irregular inflection, or — since ADR-068 narrowed the persist gate and
 * editing a Term is now routine — a Term the learner has edited to a word
 * absent from the stored sentence. In every one of those cases the raw
 * Example must never be offered: the learner has not typed anything yet, and
 * the sentence contains the answer un-blanked.
 *
 * Exclusion is by **kind**, not by label. `kind === 'example'` is the
 * canonical machine identity (ADR-040); `label` is a cosmetic, user-editable
 * display name (the "Custom name" field in the card editor) that can be
 * changed to anything, including another allowed label such as "Notes" — a
 * card so relabeled would slip past a label-string check and surface its
 * answer under a different tab. `isTermSide` already resolves Term the same
 * way; Example now follows the same rule in definition mode.
 *
 * fib mode's own tab list excludes Example for an unrelated reason — avoiding
 * a duplicate of the primary prompt, which fib mode already shows blanked —
 * and is untouched by this function: it still matches by label, computed
 * inline in LearnPage where that branch lives.
 */
export function definitionModeSupplementarySides<T extends LearnSide>(
  otherSides: readonly T[],
  isTermSide: (s: T) => boolean,
): T[] {
  return otherSides.filter(
    (s) =>
      isAllowedSide(s) &&
      !isTermSide(s) &&
      s.kind !== 'example' &&
      s.label !== 'Translation' &&
      !isImageSide(s),
  )
}
